/**
 * Switching between the flat and the 3D view. The 3D layout is only a way of looking: going to
 * 3D keeps the 2D positions aside (`SceneMemory.flatLayout`) and spreads the nodes in depth;
 * going back puts the 2D positions back, so the project's saved positions never change.
 */
import { defaultCamera } from "../camera";
import type { Scene } from "./index";
import type { Dimension, SceneMemory } from "./types";

/** The 2D / 3D switch. */
export interface SceneDimension {
  /**
   * Switches between the flat and the 3D view. Going to 3D keeps the 2D positions aside and
   * gives every node a depth; going back puts the 2D positions back. Fits the view.
   */
  setDimension(dimension: Dimension): void;
}

/** The 2D / 3D switch of `scene`. */
export function dimensionSwitch(scene: Scene, memory: SceneMemory): SceneDimension {
  /** How far the visible nodes reach from their middle, in the plane (world units). */
  const extent = (): number => {
    const visible = scene.nodes.filter((n) => n.visible);
    if (!visible.length) return 0;
    const cx = visible.reduce((sum, n) => sum + n.x, 0) / visible.length;
    const cy = visible.reduce((sum, n) => sum + n.y, 0) / visible.length;
    return Math.max(...visible.map((n) => Math.hypot(n.x - cx, n.y - cy)));
  };

  return {
    setDimension(dimension) {
      if (dimension === scene.dimension) return;
      if (dimension === "3d") {
        memory.flatLayout = new Map(scene.nodes.map((n) => [n.id, [n.x, n.y]]));
        // Start from the flat picture, each node pushed a little forward or back: the
        // simulation then spreads them in depth, so the graph "inflates" from what was shown.
        const spread = Math.max(80, extent() * 0.5);
        for (const n of scene.nodes) Object.assign(n, { z: (Math.random() - 0.5) * spread, vz: 0 });
        scene.camera = defaultCamera();
        scene.dimension = "3d";
        scene.reheat(1);
      } else {
        let moved = false;
        for (const n of scene.nodes) {
          const p = memory.flatLayout?.get(n.id);
          if (p) Object.assign(n, { x: p[0], y: p[1] });
          else moved = true;
          Object.assign(n, { z: 0, vx: 0, vy: 0, vz: 0 });
        }
        memory.flatLayout = null;
        scene.dimension = "2d";
        scene.alpha = moved ? 0.3 : 0;
      }
      scene.fit();
      scene.redraw = true;
    },
  };
}
